import { useEffect, useState } from 'react';
import { getMyAgencyDocuments } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, FileCheck2, ShieldCheck } from 'lucide-react';
import { formatDate, safeList } from './AgencyPortalShared';

const documentLabels = {
  company_registration: 'Company registration document',
  government_id: 'Government ID document',
};

function fileSize(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AgencyDocuments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyAgencyDocuments();
        if (!cancelled) setData(res.data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const documents = safeList(data?.documents);
  const missing = safeList(data?.missingDocuments);
  const byType = new Map(documents.map((document) => [document.doc_type, document]));
  const rows = ['company_registration', 'government_id'].map((type) => ({
    type,
    label: documentLabels[type],
    document: byType.get(type),
    required: data?.requiredDocumentType === type,
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Documents
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">Agency document checklist.</p>
      </div>

      {missing.length > 0 ? (
        <Card className="border border-amber-200 bg-amber-50/70 shadow-sm">
          <CardContent className="flex gap-3 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Pending document item</p>
              <p className="mt-1 text-sm text-amber-800/80">{missing.join(', ')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-emerald-200 bg-emerald-50/70 shadow-sm">
          <CardContent className="flex gap-3 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">Document checklist current</p>
              <p className="mt-1 text-sm text-emerald-800/80">MCC has the required document status on file.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Uploaded Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[1, 2].map((item) => (
                <div key={item} className="h-36 animate-pulse rounded-lg bg-[#F7F8FA]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {rows.map((row) => {
                const document = row.document;
                return (
                  <div key={row.type} className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className="flex gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#062B1A]">
                          <FileCheck2 className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#062B1A]">{row.label}</p>
                          {row.required && (
                            <Badge variant="outline" className="mt-1 border-[#D4AF37]/45 bg-[#D4AF37]/12 text-[#7A5A05]">
                              Required
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className={document ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-600'}>
                        {document ? 'On file' : 'Not on file'}
                      </Badge>
                    </div>
                    {document ? (
                      <div className="space-y-1 text-sm text-[#062B1A]/70">
                        <p className="font-medium text-[#062B1A]">{document.filename || row.label}</p>
                        <p>{[fileSize(document.size), document.uploaded_at ? `Uploaded ${formatDate(document.uploaded_at)}` : ''].filter(Boolean).join(' / ')}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-[#062B1A]/50">No file recorded.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
